import {
  Activity,
  CircleAlert,
  Clock3,
  LogOut,
  Pause,
  Play,
  RefreshCw,
  Route,
  Search,
  Send,
  Settings2,
  X
} from "lucide-react";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { QuizAnalytics } from "./QuizAnalytics.js";
import { QuizQuadrants } from "./QuizQuadrants.js";

interface Dashboard {
  submissionsToday: number;
  deliveries: Record<string, number>;
  backlog: number;
  oldestPendingAgeSeconds: number;
  endpoint: {
    id: string;
    name: string;
    enabled: boolean;
    max_requests_per_second: string;
    max_concurrency: number;
    circuit_state: string;
    paused_until: string | null;
    consecutive_failures: number;
  } | null;
}

interface Delivery {
  id: string;
  submission_id: string;
  event_id: string;
  status: string;
  attempt_count: number;
  next_attempt_at: string;
  last_http_status: number | null;
  last_error_code: string | null;
  created_at: string;
}

interface DeliveryDetails extends Delivery {
  last_error_message: string | null;
  last_response_excerpt: string | null;
  attempts: Array<{
    id: string;
    attempt_number: number;
    started_at: string;
    duration_ms: number;
    http_status: number | null;
    outcome: string;
    error_code: string | null;
    response_excerpt: string | null;
  }>;
}

type AdminView = "quiz" | "quadrants" | "deliveries";
const viewHashes: Record<AdminView, string> = { quiz: "quiz", quadrants: "cuadrantes", deliveries: "entregas" };
const viewFromHash = (hash: string): AdminView =>
  (Object.entries(viewHashes).find(([, value]) => `#${value}` === hash)?.[0] as AdminView | undefined) ?? "quiz";

export function AdminApp() {
  const [sessionState, setSessionState] = useState<"loading" | "authenticated" | "guest">("loading");
  const [csrfToken, setCsrfToken] = useState("");
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [selected, setSelected] = useState<DeliveryDetails | null>(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [notice, setNotice] = useState("");
  const [view, setView] = useState<AdminView>(() => viewFromHash(window.location.hash));
  const showView = (next: AdminView) => {
    setView(next);
    history.replaceState(null, "", `#${viewHashes[next]}`);
  };
  const onUnauthorized = useCallback(() => setSessionState("guest"), []);

  const refresh = useCallback(async () => {
    const [dashboardResponse, deliveriesResponse] = await Promise.all([
      fetch("/api/v1/admin/dashboard"),
      fetch(`/api/v1/admin/deliveries?limit=50&q=${encodeURIComponent(query)}&status=${encodeURIComponent(status)}`)
    ]);
    if (dashboardResponse.status === 401) {
      setSessionState("guest");
      return;
    }
    if (dashboardResponse.ok) setDashboard(await parseJsonResponse<Dashboard>(dashboardResponse));
    if (deliveriesResponse.ok) setDeliveries(await parseJsonResponse<Delivery[]>(deliveriesResponse) ?? []);
  }, [query, status]);

  useEffect(() => {
    void (async () => {
      try {
        const response = await fetch("/api/v1/admin/session");
        if (!response.ok) {
          setSessionState("guest");
          return;
        }
        const session = await parseJsonResponse<{ csrfToken: string }>(response);
        if (session?.csrfToken) setCsrfToken(session.csrfToken);
        setSessionState("authenticated");
        const [dashboardResponse, deliveriesResponse] = await Promise.all([
          fetch("/api/v1/admin/dashboard"),
          fetch("/api/v1/admin/deliveries?limit=50")
        ]);
        if (dashboardResponse.ok) setDashboard(await parseJsonResponse<Dashboard>(dashboardResponse));
        if (deliveriesResponse.ok) setDeliveries(await parseJsonResponse<Delivery[]>(deliveriesResponse) ?? []);
      } catch {
        setSessionState("guest");
      }
    })();
  }, []);

  async function mutate(path: string, init: RequestInit = {}) {
    const response = await fetch(path, {
      ...init,
      method: init.method ?? "POST",
      headers: {
        "content-type": "application/json",
        "x-csrf-token": csrfToken,
        ...init.headers
      }
    });
    if (response.status === 401) setSessionState("guest");
    if (!response.ok) throw new Error("The action could not be completed.");
    setNotice("Changes saved.");
    await refresh();
    return response;
  }

  if (sessionState === "loading") return <div className="admin-loading"><Activity className="spin" /> Loading</div>;
  if (sessionState === "guest") {
    return <AdminLogin onAuthenticated={(token) => {
      setCsrfToken(token);
      setSessionState("authenticated");
      void refresh();
    }} />;
  }

  return (
    <div className="admin-shell">
      <header className="admin-header">
        <a className="admin-brand" href="/admin"><Route size={22} /> <span>Pasos al Éxito</span></a>
        <nav className="admin-tabs" aria-label="Secciones">
          <button aria-current={view === "quiz" ? "page" : undefined} onClick={() => showView("quiz")}>Quiz</button>
          <button aria-current={view === "quadrants" ? "page" : undefined} onClick={() => showView("quadrants")}>Cuadrantes</button>
          <button aria-current={view === "deliveries" ? "page" : undefined} onClick={() => showView("deliveries")}>Entregas</button>
        </nav>
        <div className="admin-actions">
          <button className="icon-button" title="Refresh" aria-label="Refresh" onClick={() => void refresh()}><RefreshCw size={18} /></button>
          <button className="icon-button" title="Sign out" aria-label="Sign out" onClick={() => void mutate("/api/v1/admin/logout").finally(() => setSessionState("guest"))}><LogOut size={18} /></button>
        </div>
      </header>
      <main className="admin-main">
        {view === "quiz" ? <QuizAnalytics onUnauthorized={onUnauthorized} /> : view === "quadrants" ? <QuizQuadrants onUnauthorized={onUnauthorized} /> : <>
        <section className="page-heading">
          <div><p className="eyebrow">Delivery control</p><h1>Form operations</h1></div>
          <span className={`system-state ${dashboard?.endpoint?.circuit_state ?? "closed"}`}>
            <span /> Circuit {dashboard?.endpoint?.circuit_state ?? "unknown"}
          </span>
        </section>

        {notice && <div className="notice" role="status">{notice}<button aria-label="Dismiss" onClick={() => setNotice("")}><X size={16} /></button></div>}

        <section className="metric-grid" aria-label="Current metrics">
          <Metric icon={<Send />} label="Accepted today" value={dashboard?.submissionsToday ?? 0} />
          <Metric icon={<Activity />} label="Backlog" value={dashboard?.backlog ?? 0} />
          <Metric icon={<Clock3 />} label="Oldest pending" value={formatDuration(dashboard?.oldestPendingAgeSeconds ?? 0)} />
          <Metric icon={<CircleAlert />} label="Dead letters" value={dashboard?.deliveries["dead_letter"] ?? 0} emphasis={(dashboard?.deliveries["dead_letter"] ?? 0) > 0} />
        </section>

        {dashboard?.endpoint && (
          <EndpointControls
            key={`${dashboard.endpoint.id}-${dashboard.endpoint.max_requests_per_second}-${dashboard.endpoint.max_concurrency}-${dashboard.endpoint.paused_until ?? "active"}`}
            endpoint={dashboard.endpoint}
            mutate={mutate}
          />
        )}

        <section className="deliveries-section" aria-labelledby="deliveries-title">
          <div className="section-heading">
            <div><p className="eyebrow">Recent activity</p><h2 id="deliveries-title">Deliveries</h2></div>
            <button className="button danger-text" onClick={() => {
              if (confirm("Retry up to 25 dead-letter deliveries?")) {
                void mutate("/api/v1/admin/dead-letters/retry", {
                  body: JSON.stringify({ count: 25, confirmation: "RETRY DEAD LETTERS" })
                });
              }
            }}><RefreshCw size={16} /> Retry 25 dead letters</button>
          </div>
          <form className="filters" onSubmit={(event) => { event.preventDefault(); void refresh(); }}>
            <label className="search-field"><Search size={17} /><span className="sr-only">Search</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Submission or event ID" /></label>
            <label><span className="sr-only">Status</span><select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">All statuses</option>
              {["pending", "processing", "retry_scheduled", "delivered", "dead_letter", "cancelled"].map((value) => <option key={value} value={value}>{prettyStatus(value)}</option>)}
            </select></label>
            <button className="button secondary" type="submit">Apply</button>
          </form>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Status</th><th>Submission</th><th>Attempts</th><th>HTTP</th><th>Created</th></tr></thead>
              <tbody>
                {deliveries.map((delivery) => (
                  <tr key={delivery.id} tabIndex={0} onClick={() => void openDetails(delivery.id, setSelected)} onKeyDown={(event) => { if (event.key === "Enter") void openDetails(delivery.id, setSelected); }}>
                    <td><StatusBadge status={delivery.status} /></td>
                    <td className="mono">{delivery.submission_id}</td>
                    <td>{delivery.attempt_count}</td>
                    <td>{delivery.last_http_status ?? "—"}</td>
                    <td>{new Date(delivery.created_at).toLocaleString()}</td>
                  </tr>
                ))}
                {deliveries.length === 0 && <tr><td colSpan={5} className="empty">No deliveries match this view.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
        </>}
      </main>
      {selected && <DeliveryDrawer delivery={selected} onClose={() => setSelected(null)} mutate={mutate} />}
    </div>
  );
}

function AdminLogin({ onAuthenticated }: { onAuthenticated: (csrf: string) => void }) {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setError("");
    const data = new FormData(event.currentTarget);
    const response = await fetch("/api/v1/admin/login", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ username: data.get("username"), password: data.get("password") })
    });
    setLoading(false);
    if (!response.ok) {
      setError(response.status === 429 ? "Too many attempts. Try again shortly." : "Invalid username or password.");
      return;
    }
    const body = await parseJsonResponse<{ csrfToken: string }>(response);
    if (body?.csrfToken) onAuthenticated(body.csrfToken);
  }
  return (
    <main className="login-shell">
      <section className="login-panel">
        <a className="brand dark" href="/"><span className="brand-icon"><Route size={24} /></span><span>Pasos al Éxito</span></a>
        <div><p className="eyebrow">Restricted access</p><h1>Sign in</h1><p className="lede">Manage delivery health and recovery.</p></div>
        {error && <div className="error-summary" role="alert">{error}</div>}
        <form onSubmit={(event) => void submit(event)}>
          <div className="field"><label htmlFor="username">Username</label><input id="username" name="username" autoComplete="username" required /></div>
          <div className="field"><label htmlFor="password">Password</label><input id="password" name="password" type="password" autoComplete="current-password" required /></div>
          <button className="button primary" disabled={loading}>{loading ? "Signing in…" : "Sign in"}</button>
        </form>
      </section>
    </main>
  );
}

function Metric({ icon, label, value, emphasis = false }: { icon: React.ReactNode; label: string; value: string | number; emphasis?: boolean }) {
  return <div className={`metric ${emphasis ? "emphasis" : ""}`}><span className="metric-icon">{icon}</span><div><p>{label}</p><strong>{value}</strong></div></div>;
}

function EndpointControls({ endpoint, mutate }: { endpoint: NonNullable<Dashboard["endpoint"]>; mutate: (path: string, init?: RequestInit) => Promise<Response> }) {
  const [rps, setRps] = useState(Number(endpoint.max_requests_per_second));
  const [concurrency, setConcurrency] = useState(endpoint.max_concurrency);
  const paused = endpoint.paused_until !== null;
  return (
    <section className="endpoint-band" aria-labelledby="endpoint-title">
      <div className="section-heading"><div><p className="eyebrow">Primary endpoint</p><h2 id="endpoint-title">{endpoint.name}</h2></div><span className={`status-badge ${paused ? "paused" : "delivered"}`}>{paused ? "Paused" : "Active"}</span></div>
      <div className="capacity-form">
        <label>Requests / second<input type="number" min={1} max={10000} value={rps} onChange={(event) => setRps(Number(event.target.value))} /></label>
        <label>Max concurrency<input type="number" min={1} max={1000} value={concurrency} onChange={(event) => setConcurrency(Number(event.target.value))} /></label>
        <button className="button secondary" onClick={() => void mutate(`/api/v1/admin/endpoints/${endpoint.id}/capacity`, { method: "PATCH", body: JSON.stringify({ maxRequestsPerSecond: rps, maxConcurrency: concurrency }) })}><Settings2 size={16} /> Save capacity</button>
        <button className={`button ${paused ? "primary" : "warning"}`} onClick={() => void mutate(`/api/v1/admin/endpoints/${endpoint.id}/${paused ? "resume" : "pause"}`)}>{paused ? <Play size={16} /> : <Pause size={16} />}{paused ? "Resume" : "Pause"}</button>
      </div>
    </section>
  );
}

function DeliveryDrawer({ delivery, onClose, mutate }: { delivery: DeliveryDetails; onClose: () => void; mutate: (path: string, init?: RequestInit) => Promise<Response> }) {
  const retryable = ["dead_letter", "retry_scheduled", "cancelled"].includes(delivery.status);
  const cancellable = ["pending", "retry_scheduled", "dead_letter"].includes(delivery.status);
  async function downloadExport() {
    const response = await mutate(`/api/v1/admin/deliveries/${delivery.id}/export`);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `delivery-${delivery.id}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  }
  return (
    <div className="drawer-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <aside className="drawer" aria-modal="true" role="dialog" aria-labelledby="drawer-title">
        <header><div><p className="eyebrow">Delivery detail</p><h2 id="drawer-title">{delivery.event_id}</h2></div><button className="icon-button" onClick={onClose} aria-label="Close"><X /></button></header>
        <dl className="detail-grid"><dt>Status</dt><dd><StatusBadge status={delivery.status} /></dd><dt>Submission</dt><dd className="mono">{delivery.submission_id}</dd><dt>Attempts</dt><dd>{delivery.attempt_count}</dd><dt>Last HTTP</dt><dd>{delivery.last_http_status ?? "—"}</dd><dt>Error</dt><dd>{delivery.last_error_code ?? "—"}</dd></dl>
        <div className="drawer-actions">
          {retryable && <button className="button primary" onClick={() => void mutate(`/api/v1/admin/deliveries/${delivery.id}/retry`).then(onClose)}><RefreshCw size={16} /> Schedule retry</button>}
          {cancellable && <button className="button danger-text" onClick={() => void mutate(`/api/v1/admin/deliveries/${delivery.id}/cancel`).then(onClose)}><X size={16} /> Cancel</button>}
          <button className="button secondary" onClick={() => void downloadExport()}><Send size={16} /> Export JSON</button>
        </div>
        <h3>Attempt history</h3>
        <div className="attempt-list">
          {delivery.attempts.map((attempt) => <article key={attempt.id}><div><strong>Attempt {attempt.attempt_number}</strong><StatusBadge status={attempt.outcome} /></div><p>{new Date(attempt.started_at).toLocaleString()} · {attempt.duration_ms} ms · HTTP {attempt.http_status ?? "—"}</p>{attempt.error_code && <code>{attempt.error_code}</code>}</article>)}
          {delivery.attempts.length === 0 && <p className="empty">No attempts recorded.</p>}
        </div>
      </aside>
    </div>
  );
}

async function openDetails(id: string, setSelected: (value: DeliveryDetails) => void) {
  const response = await fetch(`/api/v1/admin/deliveries/${id}`);
  if (response.ok) {
    const details = await parseJsonResponse<DeliveryDetails>(response);
    if (details) setSelected(details);
  }
}

async function parseJsonResponse<T>(response: Response): Promise<T | null> {
  const contentLength = response.headers.get("content-length");
  if (response.status === 204 || response.status === 205) return null;
  if (contentLength === "0") return null;
  const text = await response.text();
  if (!text) return null;
  return JSON.parse(text) as T;
}

function StatusBadge({ status }: { status: string }) {
  return <span className={`status-badge ${status}`}>{prettyStatus(status)}</span>;
}

function prettyStatus(value: string) {
  return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDuration(seconds: number) {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
}
